<template>
  <v-row dense>
    <v-col cols="12">
      <!-- เวลาประเมินเป็นข้อเท็จจริงของระบบ ไม่ใช่ตัวเลือก — worker ถูกปลุกตอนเคสปิด
           (QaCaptureWriter ส่งสัญญาณ) · ตัวเลือก ทันที/ตามรอบ/สั่งเอง ของเดิมเป็นเดโม่ที่ไม่มีจริง -->
      <v-alert type="info" variant="tonal" density="compact" class="mb-3" icon="mdi-clock-fast">
        <span class="text-body-2">
          {{ $t('qaPipNote') }}
        </span>
      </v-alert>
    </v-col>

    <v-col cols="12" md="6">
      <v-card class="qa-card-sm" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaPipScopeTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaPipMode') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaPipModeDesc') }}</div>
            </div>
            <!-- ค่า 'sample' ต้องตรงกับ QaCoverageMode ฝั่ง server — ของเดิมใช้ 'sampling'
                 ซึ่ง server ไม่รู้จักและจะถอยไป 'all' เงียบ ๆ -->
            <v-btn-toggle v-model="config.coverageMode" density="compact" variant="outlined" divided mandatory rounded="lg" color="primary">
              <v-btn value="all" size="x-small">{{ $t('qaPipAll') }}</v-btn>
              <v-btn value="sample" size="x-small">{{ $t('qaPipSample') }}</v-btn>
            </v-btn-toggle>
          </div>
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaPipRate') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaPipRateDesc') }}</div>
            </div>
            <v-text-field v-model.number="config.samplingPercent" :disabled="config.coverageMode !== 'sample'" type="number" min="1" max="100" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaPipMinLines') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaPipMinLinesDesc') }}</div>
            </div>
            <v-text-field v-model.number="config.minAgentLines" type="number" min="1" max="50" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaPipMinSeconds') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaPipMinSecondsDesc') }}</div>
            </div>
            <v-text-field v-model.number="config.minDurationSec" type="number" min="0" max="3600" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12" md="6">
      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaPipExcludeTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaPipExclude') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaPipExcludeDesc') }}
              </div>
            </div>
            <!-- combobox = พิมพ์ค่าใหม่เองได้ ไม่ต้องมีรายการให้เลือก — agentKey เป็นรหัสจาก
                 Salesforce ที่ระบบนี้ไม่มีทะเบียนครบพอจะทำ dropdown -->
            <v-combobox
              v-model="config.excludeAgentKeys"
              multiple
              chips
              closable-chips
              density="compact"
              variant="outlined"
              hide-details
              :placeholder="$t('qaPipExcludePlaceholder')"
            />
          </div>
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaPipBackfill') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaPipBackfillDesc') }}
              </div>
            </div>
            <v-text-field v-model.number="config.backfillDays" type="number" min="1" max="90" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12">
      <v-btn color="primary" variant="flat" :loading="saving" prepend-icon="mdi-content-save-outline" @click="save">{{ $t('qaSetSaveConfig') }}</v-btn>
      <span v-if="updatedAt" class="text-caption text-medium-emphasis ml-3">
        {{ $t('qaSetLastEditedBy') }} {{ updatedBy || $t('qaSetUnknown') }} · {{ updatedAtText }}
      </span>
      <span v-else class="text-caption text-medium-emphasis ml-3">{{ $t('qaSetNeverSaved') }}</span>
    </v-col>
  </v-row>
</template>

<script>
/**
 * Q9 — แท็บ pipeline: เคสไหนบ้างที่ถูกประเมิน (ของจริง · cc.QaSettings['pipeline'])
 *
 * ⚠️ มีเฉพาะค่าที่ worker ใช้จริง — trigger/scheduleWindow/concurrency/retryCount/
 *    reevaluateOnRubricChange/compareVersions/retention/minTranscriptConfidence ของเดิม
 *    ถูกตัดทิ้งทั้งหมด: เวลาประเมินตายตัว (หลังจบการสนทนา) · ไม่ retry (นโยบายทุก worker) ·
 *    ไม่ประเมินซ้ำเมื่อ rubric เปลี่ยน (design เลือก none — วัดด้วยเกณฑ์ ณ ตอนที่ทำงาน) ·
 *    ช่องที่กรอกได้แต่ไม่มีผลคือการโกหกหน้าจอ
 */
import qaConsole from '@/services/qaConsole';

export default {
  name: 'QaPipelineSettings',
  emits: ['saved'],
  data() {
    return {
      // ค่าตั้งต้นตรงกับ QaPipelineSettings.Default ฝั่ง server
      config: {
        coverageMode: 'all',
        samplingPercent: 25,
        minAgentLines: 2,
        minDurationSec: 45,
        excludeAgentKeys: [],
        backfillDays: 7,
      },
      updatedAt: null,
      updatedBy: null,
      saving: false,
    };
  },
  computed: {
    updatedAtText() {
      if (!this.updatedAt) return '';
      try {
        return new Date(this.updatedAt).toLocaleString('th-TH');
      } catch {
        return this.updatedAt;
      }
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const res = await qaConsole.getPipelineConfig();
        if (res && res.value) this.config = { ...this.config, ...res.value };
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaEvalLoadFailed', { msg: err.message }) });
      }
    },
    async save() {
      this.saving = true;
      try {
        const res = await qaConsole.savePipelineConfig(this.config);
        // รับค่าที่ server คืน (clamp + ตัด agentKey ซ้ำ/ว่าง) กลับมาแสดง — ไม่ใช่ค่าที่พิมพ์
        if (res && res.value) this.config = { ...this.config, ...res.value };
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
        this.$emit('saved', { ok: true, message: this.$t('qaPipSaved') });
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaSetSaveFailed', { msg: err.message }) });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.qa-set-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}
.qa-set-row:last-of-type {
  border-bottom: none;
}
.qa-set-row > div:first-child {
  flex: 1;
  min-width: 0;
}
/* แถวที่คำอธิบายยาว — วางช่องเลือกไว้ใต้คำอธิบายแทนที่จะบีบให้อยู่ข้าง ๆ */
.qa-set-row--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
</style>
